import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Paper,
  Tooltip,
  Typography,
  makeStyles,
  TextField
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    borderRadius: 14
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2)
  },
  periods: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1)
  },
  gridWrapper: {
    overflowX: "auto"
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "56px repeat(7, minmax(88px, 1fr))",
    gap: 8,
    minWidth: 760
  },
  cell: {
    height: 28,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 700,
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      transform: "scale(1.02)",
      boxShadow: theme.shadows[3]
    }
  },
  headerCell: {
    fontWeight: 700,
    color: theme.palette.text.secondary,
    fontSize: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  hourCell: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: 12,
    display: "flex",
    alignItems: "center"
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(2),
    flexWrap: "wrap"
  },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  legendScale: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 18px)",
    gap: 6
  },
  legendSwatch: {
    height: 12,
    borderRadius: 999
  },
  loading: {
    minHeight: 220,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  ticketItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2)
  },
  ticketMeta: {
    color: theme.palette.text.secondary
  },
  customTooltip: {
    maxWidth: "none !important",
    whiteSpace: "nowrap"
  }
}));

const DAYS = [
  i18n.t("dashboard.heatmap.shortDays.sunday"),
  i18n.t("dashboard.heatmap.shortDays.monday"),
  i18n.t("dashboard.heatmap.shortDays.tuesday"),
  i18n.t("dashboard.heatmap.shortDays.wednesday"),
  i18n.t("dashboard.heatmap.shortDays.thursday"),
  i18n.t("dashboard.heatmap.shortDays.friday"),
  i18n.t("dashboard.heatmap.shortDays.saturday"),
];
const PERIODS = [7, 15, 30, 90, 365];

const AttendanceHeatmap = ({
  data,
  details,
  maxCount,
  loading
}) => {
  const classes = useStyles();
  const history = useHistory();
  const [selectedCell, setSelectedCell] = React.useState(null);

  const getIntensityColor = count => {
    if (!count || !maxCount) {
      return "rgba(120, 120, 120, 0.18)";
    }

    const ratio = count / maxCount;

    if (ratio < 0.2) return "#12314d";
    if (ratio < 0.4) return "#1f5a8c";
    if (ratio < 0.6) return "#2779bd";
    if (ratio < 0.8) return "#4fa3ff";
    return "#0a84ff";
  };

  const map = new Map((data || []).map(item => [`${item.dayOfWeek}-${item.hour}`, item.total]));
  const detailsMap = new Map((details || []).map(item => [`${item.dayOfWeek}-${item.hour}`, item]));

  const getTextColor = count => {
    if (!count || !maxCount) {
      return "transparent";
    }

    const ratio = count / maxCount;
    return ratio >= 0.6 ? "#ffffff" : "#dbeafe";
  };

  const formatHourRange = hour =>
    i18n.t("dashboard.heatmap.hourRange", {
      start: String(hour).padStart(2, "0"),
      end: String(hour).padStart(2, "0"),
    });

  const fullDayLabel = dayIndex => {
    const labels = [
      i18n.t("dashboard.heatmap.days.sunday"),
      i18n.t("dashboard.heatmap.days.monday"),
      i18n.t("dashboard.heatmap.days.tuesday"),
      i18n.t("dashboard.heatmap.days.wednesday"),
      i18n.t("dashboard.heatmap.days.thursday"),
      i18n.t("dashboard.heatmap.days.friday"),
      i18n.t("dashboard.heatmap.days.saturday"),
    ];
    return labels[dayIndex] || DAYS[dayIndex];
  };

  const openTicket = ticket => {
    if (!ticket?.ticketUuid) return;
    history.push(`/tickets/${ticket.ticketUuid}`);
  };

  return (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.toolbar}>
        <div>
          <Typography variant="h6">{i18n.t("dashboard.heatmap.title")}</Typography>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("dashboard.heatmap.description")}
          </Typography>
        </div>

      </div>

      {loading ? (
        <div className={classes.loading}>
          <CircularProgress size={28} />
        </div>
      ) : (
        <>
          <div className={classes.gridWrapper}>
            <div className={classes.grid}>
              <div className={classes.headerCell}></div>
              {DAYS.map(day => (
                <div key={day} className={classes.headerCell}>
                  {day}
                </div>
              ))}

              {Array.from({ length: 24 }).map((_, hour) => (
                <React.Fragment key={`row-${hour}`}>
                  <div className={classes.hourCell}>{String(hour).padStart(2, "0")}h</div>
                  {DAYS.map((_dayLabel, dayIndex) => {
                    const count = map.get(`${dayIndex}-${hour}`) || 0;
                    const detail = detailsMap.get(`${dayIndex}-${hour}`);
                    const previewTickets = detail?.tickets?.slice(0, 5) || [];
                    const additionalCount = Math.max(0, (detail?.tickets?.length || 0) - previewTickets.length);
                    const tooltip = (
                      <Box>
                        <Typography variant="subtitle2">{fullDayLabel(dayIndex)} | {formatHourRange(hour)}</Typography>
                        <Typography variant="body2">{i18n.t("dashboard.heatmap.attendances", { count })}</Typography>
                        {previewTickets.map(ticket => (
                          <Box key={`${ticket.ticketId}-${ticket.startedAt}`} mt={0.5}>
                            <Typography variant="caption" display="block">
                              {i18n.t("dashboard.heatmap.ticketDetails", {
                                ticketId: ticket.ticketId,
                                contact: ticket.contactName || i18n.t("dashboard.heatmap.noName"),
                                number: ticket.contactNumber || "--",
                                status: ticket.status || "--",
                                user: ticket.userName || "--",
                              })}
                            </Typography>
                          </Box>
                        ))}
                        {additionalCount > 0 && (
                          <Box mt={1}>
                            <Typography variant="caption">{i18n.t("dashboard.heatmap.additionalTickets", { count: additionalCount })}</Typography>
                          </Box>
                        )}
                      </Box>
                    );

                    return (
                      <Tooltip title={tooltip} key={`${dayIndex}-${hour}`} arrow classes={{ tooltip: classes.customTooltip }}>
                        <div
                          className={classes.cell}
                          onClick={() => detail && detail.tickets?.length > 0 && setSelectedCell(detail)}
                          style={{ backgroundColor: getIntensityColor(count), color: getTextColor(count) }}
                        >
                          {count > 0 ? count : ""}
                        </div>
                      </Tooltip>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className={classes.footer}>
            <Typography variant="body2">
              {i18n.t("dashboard.heatmap.peak", { count: maxCount || 0 })}
            </Typography>
            <div className={classes.legend}>
              <Typography variant="caption">{i18n.t("dashboard.heatmap.lower")}</Typography>
              <div className={classes.legendScale}>
                {[0, 0.2, 0.4, 0.6, 0.8].map(step => (
                  <div
                    key={step}
                    className={classes.legendSwatch}
                    style={{ backgroundColor: step === 0 ? "rgba(120, 120, 120, 0.18)" : getIntensityColor(Math.ceil(step * (maxCount || 1))) }}
                  />
                ))}
              </div>
              <Typography variant="caption">{i18n.t("dashboard.heatmap.higher")}</Typography>
            </div>
          </div>
        </>
      )}

      <Dialog
        open={Boolean(selectedCell)}
        onClose={() => setSelectedCell(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {selectedCell ? i18n.t("dashboard.heatmap.dialogTitle", {
            day: fullDayLabel(selectedCell.dayOfWeek),
            hourRange: formatHourRange(selectedCell.hour),
            count: selectedCell.total,
          }) : ""}
        </DialogTitle>
        <DialogContent dividers>
          <List>
            {(selectedCell?.tickets || []).map(ticket => (
              <ListItem button onClick={() => openTicket(ticket)} key={`${ticket.ticketId}-${ticket.startedAt}`} divider className={classes.ticketItem}>
                <Box>
                  <Typography variant="subtitle2">Ticket #{ticket.ticketId}</Typography>
                  <Typography variant="body2">{ticket.contactName || i18n.t("dashboard.heatmap.noName")}</Typography>
                  <Typography variant="caption" className={classes.ticketMeta} display="block">
                    {i18n.t("dashboard.heatmap.ticketMeta", {
                      number: ticket.contactNumber || "--",
                      status: ticket.status || "--",
                      user: ticket.userName || "--",
                    })}
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedCell(null)} color="primary">
            {i18n.t("common.buttons.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AttendanceHeatmap;
